import React, { Component, Children } from 'react';
import { css, cx } from 'emotion';
import PropTypes from 'prop-types';
import Typography from '@hig/typography';
import IconButton from '@hig/icon-button';
import { Notification16, Notification24, CloseSUI } from '@hig/icons';
import ThemeContext from '@hig/theme-context';
import { createCustomClassNames, combineEventHandlers } from '@hig/utils';
import Transition, { UNMOUNTED, EXITED, ENTERING, ENTERED, EXITING } from 'react-transition-group/Transition';
import RichText from '@hig/rich-text';
import { ControlBehavior } from '@hig/behaviors';
import { polyfill } from 'react-lifecycles-compat';
import Flyout, { transitionStatuses, AVAILABLE_TRANSITION_STATUSES, Panel, anchorPoints, AVAILABLE_ANCHOR_POINTS } from '@hig/flyout';
export { anchorPoints, AVAILABLE_ANCHOR_POINTS } from '@hig/flyout';
import ProgressRing from '@hig/progress-ring';

var types = Object.freeze({
  ERROR: "error",
  PRIMARY: "primary",
  SUCCESS: "success",
  WARNING: "warning"
});

var AVAILABLE_TYPES = Object.freeze(Object.values(types));

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function getRulesByTransitionStatus(transitionStatus) {
  if (transitionStatus === "exiting") {
    return { height: 0, opacity: 0 };
  }
  if (transitionStatus === "exited") {
    return { display: "none" };
  }
  return {};
}

function getRulesByType(type, themeData) {
  switch (type) {
    case types.ERROR:
      return { borderLeftColor: themeData["colorScheme.status.error"] };
    case types.WARNING:
      return { borderLeftColor: themeData["colorScheme.status.warning"] };
    case types.SUCCESS:
      return { borderLeftColor: themeData["colorScheme.status.success"] };
    default:
      return {
        borderLeftColor: themeData["basics.colors.primary.autodeskBlue.500"]
      };
  }
}

function stylesheet(props, themeData) {
  var hasHover = props.hasHover,
      loadingTransitionState = props.loadingTransitionState,
      showCount = props.showCount,
      customStylesheet = props.stylesheet,
      transitionStatus = props.transitionStatus,
      type = props.type,
      unread = props.unread;

  var isEntering = loadingTransitionState === "entering" || loadingTransitionState === "entered";
  var styles = {
    dismissButton: _extends({
      display: "none",
      position: "absolute",
      top: 0,
      right: 0
    }, hasHover ? { display: "block" } : {}),
    notification: _extends({
      position: "relative",
      overflow: "hidden",
      transitionProperty: "height, opacity",
      transitionDuration: "300ms",
      transitionTimingFunction: "ease",
      "&:last-child": {
        borderBottom: "none"
      }
    }, transitionStatus ? getRulesByTransitionStatus(transitionStatus) : {}, hasHover ? { backgroundColor: themeData["colorScheme.surface.level300"] } : {}),
    notificationContent: _extends({
      display: "flex",
      borderBottom: "1px solid " + themeData["divider.heavyColor"],
      borderLeft: "3px solid #bec8d2"
    }, unread ? getRulesByType(type, themeData) : { borderLeftColor: "rgba(128, 128, 128, 0.4)" }),
    notificationContentImage: {
      margin: themeData["density.spacings.small"] + " 0 " + themeData["density.spacings.small"] + " " + themeData["density.spacings.small"]
    },
    notificationContentText: {
      margin: "" + themeData["density.spacings.small"]
    },
    panelTitle: {
      padding: themeData["density.spacings.small"] + "\n        " + themeData["density.spacings.large"],
      borderBottom: "1px solid " + themeData["divider.heavyColor"]
    },
    panelContainer: {
      width: "300px",
      overflowY: "auto",
      overflowX: "hidden"
    },
    panelLoading: _extends({
      display: "flex",
      boxSizing: "border-box",
      justifyContent: "center",
      alignItems: "center",
      height: 0,
      overflow: "hidden",
      backgroundColor: "transparent",
      opacity: "0",
      transitionProperty: "height, opacity",
      transitionDuration: "300ms",
      transitionTimingFunction: "ease",
      pointerEvents: "none"
    }, isEntering ? { height: "44px", opacity: 1 } : {}),
    indicator: {
      display: "flex",
      position: "relative"
    },
    indicatorCount: _extends({
      position: "absolute",
      top: "50%",
      left: "50%",
      height: "13px",
      padding: "0 3px",
      fontSize: "11px",
      lineHeight: "13px",
      border: "1px solid " + themeData["colorScheme.surface.level100"],
      color: themeData["basics.colors.primary.white"],
      backgroundColor: "#0696d7",
      borderRadius: "4px",
      pointerEvents: "none",
      fontWeight: 400,
      fontFamily: themeData["notifications.fontFamily"],
      margin: 0
    }, showCount ? { display: "block" } : { display: "none" }),
    image: {
      height: "48px",
      width: "48px",
      overflow: "hidden",
      borderRadius: "4px"
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center"
    },
    emptyStateImage: {
      marginTop: "20px"
    },
    emptyStateMessage: {
      margin: "20px 0 37px"
    }
  };

  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

var _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function ImagePresenter(props) {
  return React.createElement("img", _extends$1({ className: css(stylesheet({}, {}).image) }, props));
}
ImagePresenter.__docgenInfo = {
  "description": "",
  "displayName": "ImagePresenter"
};

var _extends$2 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }return target;
};

function _objectWithoutProperties(obj, keys) {
  var target = {};for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;target[i] = obj[i];
  }return target;
}
var Bell = (function (_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties(_ref, ["styles"]);

  return React.createElement("svg", _extends$2({ width: "76", height: "89", viewBox: "0 0 76 89", xmlns: "http://www.w3.org/2000/svg", xmlnsXlink: "http://www.w3.org/1999/xlink" }, props), React.createElement("title", null, "empty-illustration"), React.createElement("defs", null, React.createElement("path", { id: "a", d: "M0 .347h75.716v88.291H0z" })), React.createElement("g", { fill: "none", fillRule: "evenodd" }, React.createElement("path", { d: "M1.156 61.197c0 1.867 0 3.708.026 5.228.062 5.369 3.669 10.738 10.825 14.833 14.371 8.256 37.597 8.296 51.877.094 7.083-4.07 10.636-9.406 10.665-14.75v-5.427l-73.393.022z", fill: "#B4C1C9" }), React.createElement("path", { d: "M1.157 60.994C1.22 49.357 17.702 40.012 37.969 40.12c20.268.109 36.647 9.631 36.583 21.268C74.49 73.026 58.007 82.37 37.74 82.26c-20.268-.109-36.646-9.63-36.583-21.267", fill: "#F3F5F7" }), React.createElement("path", { d: "M63.727 46.324a40.231 40.231 0 0 0-5.476-2.59L45.694 45.18l1.073 25.12c11.531-.29 15.516 2.315 21.524 2.782 9.672-8.184 8.138-19.45-4.564-26.758", fill: "#CFD8DD" }), React.createElement("path", { d: "M63.833 45.466l-16.465-.195s-6.153 13.477-6.713 13.458l-.979 9.971c.56.019 8.728-.43 13.979-3.411 7.029-4.002 13.116-9.606 11.323-14.19-.73-1.79-1.118-3.7-1.145-5.633m-51.909 0l16.465-.195s6.153 13.477 6.713 13.458l.974 9.971c-.56.019-8.727-.43-13.978-3.411-7.029-4.002-13.116-9.606-11.323-14.19a15.631 15.631 0 0 0 1.149-5.633", fill: "#7992A0" }), React.createElement("g", { transform: "translate(0 .015)" }, React.createElement("mask", { id: "b", fill: "#fff" }, React.createElement("use", { xlinkHref: "#a" })), React.createElement("path", { d: "M39.371.347a3.889 3.889 0 0 0-1.956.503L32.93 3.426c-.796.445-2.15 1.621-2.15 4.403.012.95.158 1.892.434 2.8a27.48 27.48 0 0 0-11.637 5.507c-4.945 4.063-7.662 9.486-7.636 15.27v13.416c-.244.134-.476.26-.695.387C4.404 49.156.455 54.377.04 60.021L0 61.186c0 2.102 0 3.864.026 5.235.065 6.03 4.12 11.657 11.407 15.842 7.153 4.11 16.607 6.375 26.662 6.375 9.956 0 19.323-2.232 26.374-6.28 7.207-4.15 11.207-9.743 11.247-15.75v-5.426l-.04-1.161c-.53-5.597-4.52-10.782-11.352-14.703-.168-.098-.339-.192-.51-.286V31.407c0-5.789-2.748-11.194-7.705-15.257-2.344-1.906-5-3.4-7.851-4.414v-.383c0-3.774-2.589-8.22-5.89-10.13a5.991 5.991 0 0 0-2.993-.861L39.37.347zm.004 1.176c.844.03 1.667.28 2.385.724 2.931 1.682 5.31 5.763 5.31 9.11.001.394-.037.788-.114 1.175a25.772 25.772 0 0 1 8.364 4.52c4.84 3.979 7.272 9.163 7.272 14.362v13.024c0 .438.023.876.048 1.314.364.188.727.38 1.062.579 7.16 4.113 10.77 9.486 10.829 14.858v5.427c-.03 5.347-3.582 10.68-10.666 14.75-7.083 4.07-16.44 6.125-25.789 6.125-9.425 0-18.872-2.073-26.087-6.219-7.156-4.11-10.763-9.479-10.825-14.833-.018-1.52-.026-3.361-.026-5.228-.062-5.427 3.491-10.832 10.666-14.953.41-.235.832-.463 1.258-.683.018-.362.025-.724.025-1.086V31.432c0-5.202 2.39-10.405 7.219-14.37a26.547 26.547 0 0 1 12.461-5.532 9.73 9.73 0 0 1-.818-3.686c0-1.68.6-2.855 1.567-3.398.277-.162 3.564-2.047 4.48-2.576a2.752 2.752 0 0 1 1.375-.361l.004.014z", fill: "#7F909A", mask: "url(#b)" })), React.createElement("path", { d: "M64.175 52.337a20.527 20.527 0 0 1-1.557-7.89V31.421c0-5.2-2.44-10.394-7.273-14.363-9.687-7.934-25.36-7.938-35.01 0-4.83 3.98-7.233 9.167-7.219 14.37v13.057a20.114 20.114 0 0 1-1.534 7.84c-2.138 5.043.269 10.597 7.222 14.55 10.596 6.032 27.723 6.06 38.25.073 6.826-3.882 9.186-9.649 7.12-14.612", fill: "#CFD8DD" }), React.createElement("path", { d: "M58.356 23.343a20.544 20.544 0 0 0-4.967-6.273c-4.476-3.85-10.298-5.84-16.153-5.945-6.141.105-12.236 2.091-16.912 5.937a19.83 19.83 0 0 0-6.182 8.531 1.089 1.089 0 0 0-.04.116c-.142 4.168 2.043 8.372 6.567 11.577 8.727 6.15 22.884 6.172 31.586.036 5.363-3.802 7.4-9.059 6.101-13.979", fill: "#F3F5F7" }), React.createElement("path", { d: "M59.833 22.124a21.105 21.105 0 0 0-4.499-5.065 26.608 26.608 0 0 0-11.937-5.427l-5.539 5.59c5.404 3.704 11.604 2.977 16.931 3.339 2.273.155 3.866.745 5.044 1.563", fill: "#CFD8DD" }), React.createElement("path", { d: "M75.229 61.295h-.673c0 5.38-3.538 10.752-10.665 14.833-7.127 4.08-16.451 6.125-25.818 6.125-9.43 0-18.88-2.077-26.099-6.22C4.862 71.95 1.251 66.628 1.15 61.284H.694v4.085C2.15 69.79 5.822 73.834 11.407 77.04c7.156 4.11 16.633 6.374 26.68 6.374 9.96 0 19.331-2.232 26.386-6.28 5.774-3.314 9.48-7.55 10.756-12.182v-3.657zm-35.527-43.6h3.044c1.12-.644 2.745-1.577 3.345-1.917.69-.39 1.778-1.32 2.07-3.336l-1.172-.105a5.082 5.082 0 0 1-.083.41v.025c-.09.348-.228.682-.411.99a.355.355 0 0 0-.03.055 3.748 3.748 0 0 1-.134.188l-.11.149-.123.137-.17.16a2.268 2.268 0 0 1-.415.304l-.153.086h-.026l-.65.362-.073.04-.2.115-.12.07a3.39 3.39 0 0 1-.167.093l-.091.054-.23.13-.214.127-.13.073-2.437 1.4a2.707 2.707 0 0 1-1.32.362v.028zm-8.444-6.349s2.738 5.387 6.142 5c1.415-.163-3.015-6.722-3.015-6.722l-3.127 1.722z", fill: "#7F909A" }), React.createElement("path", { d: "M47.07 11.375c0 1.675-.594 2.847-1.557 3.394-.964.546-4.491 2.575-4.491 2.575L33.495 4.472l4.501-2.587c.96-.55 2.291-.463 3.76.38 2.935 1.682 5.313 5.763 5.313 9.11", fill: "#F3F5F7" }), React.createElement("path", { d: "M45.615 6.379l-8.219 4.764 3.637 6.201s3.53-2.033 4.49-2.575c.96-.543 1.557-1.719 1.557-3.394a10.58 10.58 0 0 0-1.465-4.996", fill: "#B4C1C9" }), React.createElement("path", { d: "M33.487 4.48c2.06-1.198 5.42.71 7.507 4.259 2.086 3.55 2.107 7.399.047 8.597-2.06 1.198-5.42-.709-7.506-4.259-2.086-3.55-2.107-7.399-.048-8.597", fill: "#CFD8DD" }), React.createElement("path", { d: "M35.615 8.275c.865-.503 2.276.297 3.152 1.787.876 1.491.884 3.107.019 3.61-.865.504-2.277-.297-3.152-1.787-.876-1.49-.884-3.107-.02-3.61", fill: "#7F909A" }), React.createElement("path", { d: "M34.88 2.953h-.138c-1.266.77-1.255.763-2.767 1.7l-.299 2.135.313.43c.196-1.979 1.32-3.104 2.91-3.104.843.03 1.664.28 2.381.724 2.913 1.69 5.305 5.763 5.305 9.11 0 2.37-1.192 3.737-2.927 3.737h-.033 2.811c.826-.861 1.31-2.171 1.31-3.745 0-3.773-2.59-8.216-5.895-10.13a5.984 5.984 0 0 0-2.964-.88l-.007.023z", fill: "#7F909A" })));
});

function EmptyStatePresenter(_ref) {
  var message = _ref.message,
      customStylesheet = _ref.stylesheet;

  var styles = stylesheet({ stylesheet: customStylesheet }, {});
  return React.createElement(
    "div",
    { className: css(styles.emptyState) },
    React.createElement(Bell, { role: "presentation", className: css(styles.emptyStateImage) }),
    React.createElement(
      Typography,
      { style: styles.emptyStateMessage },
      message
    )
  );
}

EmptyStatePresenter.defaultProps = {
  message: "You currently have no notifications"
};

EmptyStatePresenter.propTypes = {
  message: PropTypes.string,
  stylesheet: PropTypes.func
};
EmptyStatePresenter.__docgenInfo = {
  "description": "",
  "displayName": "EmptyStatePresenter",
  "props": {
    "message": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "\"You currently have no notifications\"",
        "computed": false
      }
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    }
  }
};

function _objectWithoutProperties$1(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function IndicatorPresenter(props) {
  var count = props.count,
      onClick = props.onClick,
      title = props.title,
      otherProps = _objectWithoutProperties$1(props, ["count", "onClick", "title"]);

  var className = otherProps.className;

  var indicatorClassName = createCustomClassNames(className, "indicator");
  var indicatorCountClassName = createCustomClassNames(className, "indicator-count");

  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref) {
      var resolvedRoles = _ref.resolvedRoles,
          metadata = _ref.metadata;

      var styles = stylesheet(props, resolvedRoles);
      var NotificationIcon = metadata.densityId === "high-density" ? Notification16 : Notification24;
      return React.createElement(
        "div",
        { className: cx([indicatorClassName, css(styles.indicator)]) },
        React.createElement(IconButton, {
          onClick: onClick,
          icon: React.createElement(NotificationIcon, null),
          title: title
        }),
        React.createElement(
          "div",
          {
            className: cx([indicatorCountClassName, css(styles.indicatorCount)])
          },
          count
        )
      );
    }
  );
}

IndicatorPresenter.defaultProps = {
  title: "View notifications"
};

IndicatorPresenter.propTypes = {
  count: PropTypes.number,
  onClick: PropTypes.func,
  title: PropTypes.string
};
IndicatorPresenter.__docgenInfo = {
  "description": "",
  "displayName": "IndicatorPresenter",
  "props": {
    "count": {
      "type": {
        "name": "number"
      },
      "required": false,
      "description": ""
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "title": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "\"View notifications\"",
        "computed": false
      }
    }
  }
};

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var TRANSITION_DURATION = 300;

/**
 * @typedef {Object} Payload
 * @property {string} transitionStatus
 * @property {function(MouseEvent): void} handleDismissButtonClick
 */

var NotificationBehavior = function (_Component) {
  _inherits(NotificationBehavior, _Component);

  function NotificationBehavior() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck(this, NotificationBehavior);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn(this, (_ref = NotificationBehavior.__proto__ || Object.getPrototypeOf(NotificationBehavior)).call.apply(_ref, [this].concat(args))), _this), _this.state = {
      height: "",
      isVisible: true
    }, _this.refContainer = function (containerRef) {
      _this.containerRef = containerRef;
    }, _this.handleExit = function () {
      var onDismiss = _this.props.onDismiss;


      if (onDismiss) onDismiss();
    }, _this.handleDismissButtonClick = function () {
      _this.hide();
    }, _temp), _possibleConstructorReturn(_this, _ret);
  }

  /**
   * @type {Object}
   * @property {string} height Inline style height of the Notification wrapper
   * @property {boolean} isVisible Determines whether the state of the visibility transition
   */


  /**
   * @param {HTMLDivElement} containerRef
   */


  /** @type {HTMLDivElement} */


  _createClass(NotificationBehavior, [{
    key: "prepareHideTransition",


    /**
     * Sets the current height of the notification
     * so that the height transition can occur
     */
    value: function prepareHideTransition() {
      var _this2 = this;

      return new Promise(function (resolve) {
        _this2.setState({ height: _this2.containerRef.clientHeight + "px" }, function () {
          window.requestAnimationFrame(resolve);
        });
      });
    }

    /**
     * Begins the hide transition by setting the `isVisible` prop
     * The height is then controlled via CSS in the `NotificationPresenter`
     *
     * This method calls `requestAnimationFrame` again to ensure that a repaint occurs.
     * @see https://stackoverflow.com/a/42302185
     */

  }, {
    key: "startHideTransition",
    value: function startHideTransition() {
      var _this3 = this;

      window.requestAnimationFrame(function () {
        _this3.setState({
          height: "",
          isVisible: false
        });
      });
    }
  }, {
    key: "hide",
    value: function hide() {
      var _this4 = this;

      this.prepareHideTransition().then(function () {
        return _this4.startHideTransition();
      });
    }
  }, {
    key: "render",
    value: function render() {
      var innerRef = this.refContainer,
          handleDismissButtonClick = this.handleDismissButtonClick;
      var children = this.props.children;
      var _state = this.state,
          height = _state.height,
          isVisible = _state.isVisible;


      return React.createElement(
        Transition,
        {
          "in": isVisible,
          onExited: this.handleExit,
          timeout: TRANSITION_DURATION
        },
        function (transitionStatus) {
          return children({
            handleDismissButtonClick: handleDismissButtonClick,
            height: height,
            innerRef: innerRef,
            transitionStatus: transitionStatus
          });
        }
      );
    }
  }]);

  return NotificationBehavior;
}(Component);

NotificationBehavior.propTypes = {
  /** Notification content */
  children: PropTypes.func.isRequired,
  /** A callback called when user dismisses a featured notification */
  onDismiss: PropTypes.func
};
NotificationBehavior.__docgenInfo = {
  "description": "@typedef {Object} Payload\n@property {string} transitionStatus\n@property {function(MouseEvent): void} handleDismissButtonClick",
  "displayName": "NotificationBehavior",
  "props": {
    "children": {
      "type": {
        "name": "func"
      },
      "required": true,
      "description": "Notification content"
    },
    "onDismiss": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "A callback called when user dismisses a featured notification"
    }
  }
};

function _objectWithoutProperties$2(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function DismissButtonPresenter(_ref) {
  var hasHover = _ref.hasHover,
      onClick = _ref.onClick,
      customStylesheet = _ref.stylesheet,
      title = _ref.title,
      otherProps = _objectWithoutProperties$2(_ref, ["hasHover", "onClick", "stylesheet", "title"]);

  var className = otherProps.className;

  var dismissButtonClassName = createCustomClassNames(className, "dismiss-button");
  var styles = stylesheet({ hasHover: hasHover, stylesheet: customStylesheet }, {});
  return React.createElement(
    "div",
    { className: cx([dismissButtonClassName, css(styles.dismissButton)]) },
    React.createElement(IconButton, { onClick: onClick, icon: React.createElement(CloseSUI, null), title: title })
  );
}

DismissButtonPresenter.defaultProps = {
  title: "Dismiss featured notification"
};

DismissButtonPresenter.propTypes = {
  hasHover: PropTypes.bool,
  onClick: PropTypes.func,
  stylesheet: PropTypes.func,
  title: PropTypes.string
};
DismissButtonPresenter.__docgenInfo = {
  "description": "",
  "displayName": "DismissButtonPresenter",
  "props": {
    "hasHover": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "title": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "\"Dismiss featured notification\"",
        "computed": false
      }
    }
  }
};

function _objectWithoutProperties$3(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function NotificationPresenter(props) {
  var children = props.children,
      dismissButtonTitle = props.dismissButtonTitle,
      hasHover = props.hasHover,
      height = props.height,
      image = props.image,
      innerRef = props.innerRef,
      onDismissButtonClick = props.onDismissButtonClick,
      onMouseEnter = props.onMouseEnter,
      onMouseLeave = props.onMouseLeave,
      showDismissButton = props.showDismissButton,
      customStylesheet = props.stylesheet,
      timestamp = props.timestamp,
      otherProps = _objectWithoutProperties$3(props, ["children", "dismissButtonTitle", "hasHover", "height", "image", "innerRef", "onDismissButtonClick", "onMouseEnter", "onMouseLeave", "showDismissButton", "stylesheet", "timestamp"]);

  var className = otherProps.className;

  var notificationContentClassName = createCustomClassNames(className, "notification-content");
  var notificationContentImageClassName = createCustomClassNames(className, "notification-content-image");
  var notificationContentTextClassName = createCustomClassNames(className, "notification-content-text");

  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref) {
      var resolvedRoles = _ref.resolvedRoles;

      var styles = stylesheet(props, resolvedRoles);
      return React.createElement(
        "div",
        {
          className: cx([className, css(styles.notification)]),
          onMouseEnter: onMouseEnter,
          onMouseLeave: onMouseLeave,
          ref: innerRef,
          role: "listitem",
          style: { height: height }
        },
        React.createElement(
          "div",
          {
            className: cx([notificationContentClassName, css(styles.notificationContent)])
          },
          image ? React.createElement(
            "div",
            {
              className: cx([notificationContentImageClassName, css(styles.notificationContentImage)])
            },
            image
          ) : null,
          React.createElement(
            "div",
            {
              className: cx([notificationContentTextClassName, css(styles.notificationContentText)])
            },
            React.createElement(
              RichText,
              { size: "small" },
              children
            ),
            timestamp,
            showDismissButton ? React.createElement(DismissButtonPresenter, {
              className: className,
              hasHover: hasHover,
              onClick: onDismissButtonClick,
              stylesheet: customStylesheet,
              title: dismissButtonTitle
            }) : null
          )
        )
      );
    }
  );
}

NotificationPresenter.propTypes = {
  children: PropTypes.node,
  dismissButtonTitle: PropTypes.string,
  hasHover: PropTypes.bool,
  height: PropTypes.string,
  image: PropTypes.node,
  innerRef: PropTypes.func,
  onDismissButtonClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  showDismissButton: PropTypes.bool,
  stylesheet: PropTypes.func,
  timestamp: PropTypes.node
};
NotificationPresenter.__docgenInfo = {
  "description": "",
  "displayName": "NotificationPresenter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "dismissButtonTitle": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "hasHover": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "height": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "image": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "innerRef": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onDismissButtonClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseEnter": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseLeave": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "showDismissButton": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "timestamp": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    }
  }
};

var _createClass$1 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$4(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$1(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var Notification = function (_Component) {
  _inherits$1(Notification, _Component);

  function Notification() {
    _classCallCheck$1(this, Notification);

    return _possibleConstructorReturn$1(this, (Notification.__proto__ || Object.getPrototypeOf(Notification)).apply(this, arguments));
  }

  _createClass$1(Notification, [{
    key: "renderChildren",


    /**
     * @param {NotificationShape} shape
     * @returns {import("react").ReactElement}
     */
    value: function renderChildren() {
      var _props = this.props,
          children = _props.children,
          hideFlyout = _props.hideFlyout,
          dismiss = _props.onDismiss;


      if (typeof children !== "function") {
        return children;
      }

      return children({ dismiss: dismiss, hideFlyout: hideFlyout });
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _props2 = this.props,
          dismissButtonTitle = _props2.dismissButtonTitle,
          featured = _props2.featured,
          image = _props2.image,
          onDismiss = _props2.onDismiss,
          onMouseEnter = _props2.onMouseEnter,
          onMouseLeave = _props2.onMouseLeave,
          _props2$showDismissBu = _props2.showDismissButton,
          showDismissButton = _props2$showDismissBu === undefined ? featured : _props2$showDismissBu,
          stylesheet = _props2.stylesheet,
          timestamp = _props2.timestamp,
          type = _props2.type,
          unread = _props2.unread,
          otherProps = _objectWithoutProperties$4(_props2, ["dismissButtonTitle", "featured", "image", "onDismiss", "onMouseEnter", "onMouseLeave", "showDismissButton", "stylesheet", "timestamp", "type", "unread"]);

      var className = otherProps.className;


      return React.createElement(
        NotificationBehavior,
        { onDismiss: onDismiss },
        function (_ref) {
          var handleDismissButtonClick = _ref.handleDismissButtonClick,
              height = _ref.height,
              innerRef = _ref.innerRef,
              transitionStatus = _ref.transitionStatus;
          return React.createElement(
            ControlBehavior,
            {
              onMouseEnter: onMouseEnter,
              onMouseLeave: onMouseLeave
            },
            function (_ref2) {
              var hasHover = _ref2.hasHover,
                  handleMouseEnter = _ref2.onMouseEnter,
                  handleMouseLeave = _ref2.onMouseLeave;
              return React.createElement(
                NotificationPresenter,
                {
                  className: className,
                  dismissButtonTitle: dismissButtonTitle,
                  featured: featured,
                  hasHover: hasHover,
                  height: height,
                  image: image,
                  innerRef: innerRef,
                  onDismissButtonClick: handleDismissButtonClick,
                  onMouseEnter: handleMouseEnter,
                  onMouseLeave: handleMouseLeave,
                  showDismissButton: showDismissButton,
                  stylesheet: stylesheet,
                  timestamp: timestamp,
                  transitionStatus: transitionStatus,
                  type: type,
                  unread: unread
                },
                _this2.renderChildren()
              );
            }
          );
        }
      );
    }
  }]);

  return Notification;
}(Component);

Notification.propTypes = {
  /** Notification content */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /** Title HTML attribute for the dismiss button */
  dismissButtonTitle: PropTypes.string,
  /** Determines whether the notification is featured */
  featured: PropTypes.bool,
  /** An action provided hide the flyout. This is provided to the `children` render prop */
  hideFlyout: PropTypes.func,
  /** An image to display such as an avatar or and icon */
  image: PropTypes.node,
  /** A callback called when user dismisses a featured notification */
  onDismiss: PropTypes.func,
  /**
   * Triggers when the user's mouse is over the notification
   */
  onMouseEnter: PropTypes.func,
  /**
   * Triggers when the user's mouse is no longer over the notification
   */
  onMouseLeave: PropTypes.func,
  /** Determines whether the dismiss button is shown */
  showDismissButton: PropTypes.bool,
  /** Function to modify the component's styles */
  stylesheet: PropTypes.func,
  /** Timestamp component */
  timestamp: PropTypes.node,
  /** Determines notification variant */
  type: PropTypes.string,
  /** Determines whether notification has not been read */
  unread: PropTypes.bool
};
Notification.defaultProps = {
  /**
   * This is an action that's provided to the consumer,
   * as a result a value must always be available.
   */
  hideFlyout: function hideFlyout() {}
};
Notification.__docgenInfo = {
  "description": "",
  "displayName": "Notification",
  "props": {
    "children": {
      "type": {
        "name": "union",
        "value": [{
          "name": "node"
        }, {
          "name": "func"
        }]
      },
      "required": false,
      "description": "Notification content"
    },
    "dismissButtonTitle": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Title HTML attribute for the dismiss button"
    },
    "featured": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Determines whether the notification is featured"
    },
    "hideFlyout": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "An action provided hide the flyout. This is provided to the `children` render prop",
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      }
    },
    "image": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "An image to display such as an avatar or and icon"
    },
    "onDismiss": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "A callback called when user dismisses a featured notification"
    },
    "onMouseEnter": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when the user's mouse is over the notification"
    },
    "onMouseLeave": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when the user's mouse is no longer over the notification"
    },
    "showDismissButton": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Determines whether the dismiss button is shown"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    },
    "timestamp": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Timestamp component"
    },
    "type": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Determines notification variant"
    },
    "unread": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Determines whether notification has not been read"
    }
  }
};

var _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties$5(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

/** @typedef {null|undefined|NotificationElements} Input */

/**
 * @typedef {Object} ParsedNotification
 * @property {string} id
 * @property {string} key
 * @property {NotificationContent} [content]
 * @property {boolean} [featured]
 * @property {function(): void} [onDismiss]
 * @property {boolean} [showDismissButton]
 * @property {import("react").ReactElement} [timestamp]
 * @property {string} [type]
 * @property {boolean} unread
 */

/**
 * Converts the given notifications input into an array
 * @param {Input} input
 * @returns {Object[]}
 */
function normalizeInput(input) {
  if (input == null) {
    return [];
  }

  if (Array.isArray(input)) {
    return input;
  }

  var element = Children.only(input);

  if (element.type === Notification) {
    return [element];
  }

  throw new Error("Invalid notifications value.");
}

/**
 * @param {Object} value
 * @param {number} index
 * @returns {ParsedNotification}
 */
function parseNotification(value, index) {
  var _value$key = value.key,
      key = _value$key === undefined ? "notification-" + index : _value$key,
      otherValues = _objectWithoutProperties$5(value, ["key"]);

  var _ref = otherValues.props || otherValues,
      children = _ref.children,
      _ref$content = _ref.content,
      content = _ref$content === undefined ? children : _ref$content,
      _ref$id = _ref.id,
      id = _ref$id === undefined ? index.toString() : _ref$id,
      _ref$unread = _ref.unread,
      unread = _ref$unread === undefined ? true : _ref$unread,
      props = _objectWithoutProperties$5(_ref, ["children", "content", "id", "unread"]);

  return _extends$3({
    id: id,
    key: key,
    content: content,
    unread: unread
  }, props);
}

/**
 * @param {Input} input
 * @returns {ParsedNotification[]}
 */
function parseNotifications(input) {
  return normalizeInput(input).map(parseNotification);
}

var _extends$4 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass$2 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$6(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$2(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$2(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$2(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

/** @typedef {import("./parseNotifications").Input} NotificationsInput */
/** @typedef {import("./parseNotifications").ParsedNotification} ParsedNotification */
/** @typedef {import("./NotificationBehavior").Payload} NotificationBehaviorPayload */

/**
 * @typedef {Object} Payload
 * @property {Function} dismissNotification
 * @property {ParsedNotification[]} notifications
 * @property {boolean} showUnreadCount
 * @property {number} unreadCount
 */

/**
 * @typedef {Object} Props
 * @property {function(Payload): import("react").ReactElement} [children]
 * @property {NotificationsInput} [notifications]
 * @property {number} [unreadCount]
 */

/**
 * @typedef {Object} State
 * @property {string[]} dismissedNotifications An array of notification IDs that have been dismissed
 * @property {ParsedNotification[]} notifications
 * @property {string[]} readNotifications An array of notification IDs that have been read
 */

var NotificationFlyoutBehavior = function (_Component) {
  _inherits$2(NotificationFlyoutBehavior, _Component);

  function NotificationFlyoutBehavior() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck$2(this, NotificationFlyoutBehavior);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn$2(this, (_ref = NotificationFlyoutBehavior.__proto__ || Object.getPrototypeOf(NotificationFlyoutBehavior)).call.apply(_ref, [this].concat(args))), _this), _this.state = {
      dismissedNotifications: [],
      notifications: [],
      readNotifications: []
    }, _this.dismissNotification = function (id) {
      _this.setState({
        dismissedNotifications: _this.state.dismissedNotifications.concat(id)
      });
    }, _this.handleClose = function () {
      _this.markAllNotificationsRead();
    }, _temp), _possibleConstructorReturn$2(_this, _ret);
  }

  /** @type {State} */


  _createClass$2(NotificationFlyoutBehavior, [{
    key: "getNotifications",


    /**
     * @returns {ParsedNotification[]}
     */
    value: function getNotifications() {
      var _state = this.state,
          dismissedNotifications = _state.dismissedNotifications,
          notifications = _state.notifications,
          readNotifications = _state.readNotifications;


      var updateReadStatus = function updateReadStatus(_ref2) {
        var id = _ref2.id,
            unread = _ref2.unread,
            otherProps = _objectWithoutProperties$6(_ref2, ["id", "unread"]);

        return _extends$4({
          id: id,
          unread: unread && !readNotifications.includes(id)
        }, otherProps);
      };
      var isNotDismissed = function isNotDismissed(_ref3) {
        var id = _ref3.id;
        return !dismissedNotifications.includes(id);
      };

      return notifications.map(updateReadStatus).filter(isNotDismissed);
    }

    /** @returns {number} */

  }, {
    key: "getUnreadCount",
    value: function getUnreadCount() {
      var controlledUnreadCount = this.props.unreadCount;


      return controlledUnreadCount !== undefined ? controlledUnreadCount : this.deriveUnreadCount();
    }

    /**
     * @param {ParsedNotification[]} notifications
     */

  }, {
    key: "deriveUnreadCount",
    value: function deriveUnreadCount() {
      return this.getNotifications().reduce(function (count, _ref4) {
        var unread = _ref4.unread;
        return unread ? count + 1 : count;
      }, 0);
    }

    /** @type {Props} */

  }, {
    key: "markAllNotificationsRead",
    value: function markAllNotificationsRead() {
      var notifications = this.getNotifications();
      var readNotifications = this.state.readNotifications;

      var nextRead = notifications.reduce(function (result, notification) {
        var id = notification.id;


        if (!result.includes(id)) result.push(id);

        return result;
      }, readNotifications.slice());

      this.setState({ readNotifications: nextRead });
    }

    /**
     * Action to dismiss a notification
     * @param {string} id
     */


    /**
     * Handler for when the flyout opens
     */

  }, {
    key: "render",


    /**
     * @returns {import("react").ReactElement}
     */
    value: function render() {
      var dismissNotification = this.dismissNotification,
          handleClose = this.handleClose;

      var notifications = this.getNotifications();
      var unreadCount = this.getUnreadCount();
      var showUnreadCount = unreadCount > 0;

      return this.props.children({
        dismissNotification: dismissNotification,
        handleClose: handleClose,
        notifications: notifications,
        showUnreadCount: showUnreadCount,
        unreadCount: unreadCount
      });
    }
  }], [{
    key: "getDerivedStateFromProps",


    /**
     * @param {Props} nextProps
     * @returns {State | null}
     */
    value: function getDerivedStateFromProps(nextProps) {
      return {
        notifications: parseNotifications(nextProps.notifications)
      };
    }
  }]);

  return NotificationFlyoutBehavior;
}(Component);

NotificationFlyoutBehavior.propTypes = {
  /** Render prop */
  children: PropTypes.func.isRequired,
  /** Rendered notifications */
  notifications: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]))]),
  /** When provided, it overrides the derived unread notification count */
  unreadCount: PropTypes.number
};


var NotificationFlyoutBehavior$1 = polyfill(NotificationFlyoutBehavior);
NotificationFlyoutBehavior.__docgenInfo = {
  "description": "@typedef {Object} State\n@property {string[]} dismissedNotifications An array of notification IDs that have been dismissed\n@property {ParsedNotification[]} notifications\n@property {string[]} readNotifications An array of notification IDs that have been read",
  "displayName": "NotificationFlyoutBehavior",
  "props": {
    "children": {
      "type": {
        "name": "func"
      },
      "required": true,
      "description": "Render prop"
    },
    "notifications": {
      "type": {
        "name": "union",
        "value": [{
          "name": "node"
        }, {
          "name": "arrayOf",
          "value": {
            "name": "union",
            "value": [{
              "name": "node"
            }, {
              "name": "shape",
              "value": {}
            }]
          }
        }]
      },
      "required": false,
      "description": "Rendered notifications"
    },
    "unreadCount": {
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "When provided, it overrides the derived unread notification count"
    }
  }
};

var _createClass$3 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck$3(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$3(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$3(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

/** 50px per the design spec plus 30px for the height of the title */
var BOTTOM_SPACING = 80;
var TRANSITION_DURATION$1 = 300;

/**
 * @param {HTMLDivElement} listWrapper
 * @returns {string}
 */
function calculateListMaxHeight(listWrapper) {
  if (!listWrapper) return "";

  var _listWrapper$getBound = listWrapper.getBoundingClientRect(),
      listWrapperTop = _listWrapper$getBound.top;

  // Distance between the top of the list wrapper and the spacing at the bottom of the screen


  var height = window.innerHeight - BOTTOM_SPACING - listWrapperTop;

  return height + "px";
}

var PanelBehavior = function (_Component) {
  _inherits$3(PanelBehavior, _Component);

  function PanelBehavior() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck$3(this, PanelBehavior);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn$3(this, (_ref = PanelBehavior.__proto__ || Object.getPrototypeOf(PanelBehavior)).call.apply(_ref, [this].concat(args))), _this), _this.state = {
      listMaxHeight: ""
    }, _this.handleResize = function () {
      _this.updateMaxHeight();
    }, _this.refListWrapper = function (listWrapperRef) {
      _this.listWrapperRef = listWrapperRef;
    }, _temp), _possibleConstructorReturn$3(_this, _ret);
  }

  _createClass$3(PanelBehavior, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.bindResize();
      this.updateMaxHeight();
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(previousProps) {
      var _this2 = this;

      if (previousProps.transitionStatus === transitionStatuses.HIDDEN && this.props.transitionStatus === transitionStatuses.EXITED) {
        window.requestAnimationFrame(function () {
          _this2.updateMaxHeight();
        });
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.unbindResize();
    }

    /**
     * @type {HTMLDivElement}
     */

  }, {
    key: "updateMaxHeight",
    value: function updateMaxHeight() {
      this.setState({
        listMaxHeight: calculateListMaxHeight(this.listWrapperRef)
      });
    }
  }, {
    key: "bindResize",
    value: function bindResize() {
      window.addEventListener("resize", this.handleResize);
    }
  }, {
    key: "unbindResize",
    value: function unbindResize() {
      window.removeEventListener("resize", this.handleResize);
    }

    /**
     * @param {HTMLDivElement} listWrapperRef
     */

  }, {
    key: "render",
    value: function render() {
      var refListWrapper = this.refListWrapper;
      var _props = this.props,
          children = _props.children,
          loading = _props.loading;
      var listMaxHeight = this.state.listMaxHeight;


      return React.createElement(
        Transition,
        { "in": loading, timeout: TRANSITION_DURATION$1 },
        function (loadingTransitionState) {
          return children({
            loadingTransitionState: loadingTransitionState,
            listMaxHeight: listMaxHeight,
            refListWrapper: refListWrapper
          });
        }
      );
    }
  }]);

  return PanelBehavior;
}(Component);


PanelBehavior.propTypes = {
  children: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  transitionStatus: PropTypes.oneOf(AVAILABLE_TRANSITION_STATUSES)
};

PanelBehavior.defaultProps = {
  loading: false
};
PanelBehavior.__docgenInfo = {
  "description": "",
  "displayName": "PanelBehavior",
  "props": {
    "children": {
      "type": {
        "name": "func"
      },
      "required": true,
      "description": ""
    },
    "loading": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "false",
        "computed": false
      }
    },
    "transitionStatus": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_TRANSITION_STATUSES"
      },
      "required": false,
      "description": ""
    }
  }
};

function PanelPresenter(_ref) {
  var children = _ref.children,
      heading = _ref.heading,
      innerRef = _ref.innerRef,
      listMaxHeight = _ref.listMaxHeight,
      loadingTransitionState = _ref.loadingTransitionState,
      onScroll = _ref.onScroll,
      refListWrapper = _ref.refListWrapper,
      customStylesheet = _ref.stylesheet;

  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref2) {
      var resolvedRoles = _ref2.resolvedRoles;

      var styles = stylesheet({
        transitionState: null,
        loadingTransitionState: loadingTransitionState,
        stylesheet: customStylesheet
      }, resolvedRoles);
      return React.createElement(
        Panel,
        { innerRef: innerRef },
        React.createElement(
          Typography,
          { elementType: "header", style: styles.panelTitle },
          heading
        ),
        React.createElement(
          "section",
          {
            className: css(styles.panelContainer),
            ref: refListWrapper,
            style: { maxHeight: listMaxHeight }
          },
          React.createElement(
            "div",
            { role: "list", onScroll: onScroll },
            children
          )
        ),
        React.createElement(
          "footer",
          { className: css(styles.panelLoading) },
          React.createElement(ProgressRing, { size: "s" })
        )
      );
    }
  );
}

PanelPresenter.defaultProps = {
  heading: "Notifications"
};

PanelPresenter.propTypes = {
  children: PropTypes.node,
  heading: PropTypes.node,
  innerRef: PropTypes.func.isRequired,
  listMaxHeight: PropTypes.string,
  loadingTransitionState: PropTypes.oneOf([UNMOUNTED, EXITED, ENTERING, ENTERED, EXITING]),
  onScroll: PropTypes.func,
  refListWrapper: PropTypes.func,
  stylesheet: PropTypes.func
};
PanelPresenter.__docgenInfo = {
  "description": "",
  "displayName": "PanelPresenter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "heading": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "\"Notifications\"",
        "computed": false
      }
    },
    "innerRef": {
      "type": {
        "name": "func"
      },
      "required": true,
      "description": ""
    },
    "listMaxHeight": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "loadingTransitionState": {
      "type": {
        "name": "enum",
        "value": [{
          "value": "import {\n  UNMOUNTED,\n  EXITED,\n  ENTERING,\n  ENTERED,\n  EXITING\n} from \"react-transition-group/Transition\";",
          "computed": true
        }, {
          "value": "import {\n  UNMOUNTED,\n  EXITED,\n  ENTERING,\n  ENTERED,\n  EXITING\n} from \"react-transition-group/Transition\";",
          "computed": true
        }, {
          "value": "import {\n  UNMOUNTED,\n  EXITED,\n  ENTERING,\n  ENTERED,\n  EXITING\n} from \"react-transition-group/Transition\";",
          "computed": true
        }, {
          "value": "import {\n  UNMOUNTED,\n  EXITED,\n  ENTERING,\n  ENTERED,\n  EXITING\n} from \"react-transition-group/Transition\";",
          "computed": true
        }, {
          "value": "import {\n  UNMOUNTED,\n  EXITED,\n  ENTERING,\n  ENTERED,\n  EXITING\n} from \"react-transition-group/Transition\";",
          "computed": true
        }]
      },
      "required": false,
      "description": ""
    },
    "onScroll": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "refListWrapper": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    }
  }
};

function Panel$1(props) {
  var children = props.children,
      heading = props.heading,
      innerRef = props.innerRef,
      loading = props.loading,
      onScroll = props.onScroll,
      transitionStatus = props.transitionStatus,
      stylesheet = props.stylesheet;


  return React.createElement(
    PanelBehavior,
    { loading: loading, transitionStatus: transitionStatus },
    function (_ref) {
      var listMaxHeight = _ref.listMaxHeight,
          loadingTransitionState = _ref.loadingTransitionState,
          refListWrapper = _ref.refListWrapper;
      return React.createElement(
        PanelPresenter,
        {
          heading: heading,
          innerRef: innerRef,
          listMaxHeight: listMaxHeight,
          loadingTransitionState: loadingTransitionState,
          onScroll: onScroll,
          refListWrapper: refListWrapper,
          stylesheet: stylesheet
        },
        children
      );
    }
  );
}

Panel$1.propTypes = {
  children: PropTypes.node,
  heading: PropTypes.string,
  innerRef: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onScroll: PropTypes.func,
  stylesheet: PropTypes.func,
  transitionStatus: PropTypes.string
};
Panel$1.__docgenInfo = {
  "description": "",
  "displayName": "Panel",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "heading": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "innerRef": {
      "type": {
        "name": "func"
      },
      "required": true,
      "description": ""
    },
    "loading": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "onScroll": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "transitionStatus": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    }
  }
};

function _objectWithoutProperties$7(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

/** @typedef {import("./behaviors/parseNotifications").ParsedNotification} ParsedNotification */

/**
 * @param {Object} payload
 * @returns {function(ParsedNotification): JSX}
 */
function createNotificationRenderer(_ref) {
  var hideFlyout = _ref.hideFlyout,
      dismissNotification = _ref.dismissNotification;

  /* eslint-disable-next-line react/prop-types */
  return function renderNotification(notification) {
    var content = notification.content,
        dismissButtonTitle = notification.dismissButtonTitle,
        featured = notification.featured,
        id = notification.id,
        image = notification.image,
        key = notification.key,
        onDismiss = notification.onDismiss,
        showDismissButton = notification.showDismissButton,
        stylesheet = notification.stylesheet,
        timestamp = notification.timestamp,
        type = notification.type,
        unread = notification.unread,
        otherProps = _objectWithoutProperties$7(notification, ["content", "dismissButtonTitle", "featured", "id", "image", "key", "onDismiss", "showDismissButton", "stylesheet", "timestamp", "type", "unread"]);

    var className = otherProps.className;


    var handleDismiss = combineEventHandlers(onDismiss, function () {
      return dismissNotification(id);
    });

    return React.createElement(
      Notification,
      {
        className: className,
        dismissButtonTitle: dismissButtonTitle,
        featured: featured,
        hideFlyout: hideFlyout,
        image: image,
        key: key,
        onDismiss: handleDismiss,
        showDismissButton: showDismissButton,
        stylesheet: stylesheet,
        timestamp: timestamp,
        type: type,
        unread: unread
      },
      content
    );
  };
}

function createPanelRenderer(_ref2) {
  var emptyMessage = _ref2.emptyMessage,
      dismissNotification = _ref2.dismissNotification,
      notifications = _ref2.notifications,
      loading = _ref2.loading,
      heading = _ref2.heading,
      stylesheet = _ref2.stylesheet;

  return function renderPanel(_ref3) {
    var hideFlyout = _ref3.hideFlyout,
        handleScroll = _ref3.handleScroll,
        innerRef = _ref3.innerRef,
        transitionStatus = _ref3.transitionStatus;

    var isEmpty = notifications.length === 0;

    return React.createElement(
      Panel$1,
      {
        heading: heading,
        innerRef: innerRef,
        loading: loading,
        onScroll: handleScroll,
        transitionStatus: transitionStatus,
        stylesheet: stylesheet
      },
      isEmpty ? React.createElement(EmptyStatePresenter, { message: emptyMessage, stylesheet: stylesheet }) : notifications.map(createNotificationRenderer({ hideFlyout: hideFlyout, dismissNotification: dismissNotification }))
    );
  };
}

function NotificationsFlyout(props) {
  var alterCoordinates = props.alterCoordinates,
      anchorPoint = props.anchorPoint,
      children = props.children,
      emptyMessage = props.emptyMessage,
      fallbackAnchorPoints = props.fallbackAnchorPoints,
      heading = props.heading,
      indicatorTitle = props.indicatorTitle,
      loading = props.loading,
      onClick = props.onClick,
      onClickOutside = props.onClickOutside,
      onScroll = props.onScroll,
      open = props.open,
      _props$notifications = props.notifications,
      notificationsInput = _props$notifications === undefined ? children : _props$notifications,
      controlledUnreadCount = props.unreadCount,
      stylesheet = props.stylesheet,
      otherProps = _objectWithoutProperties$7(props, ["alterCoordinates", "anchorPoint", "children", "emptyMessage", "fallbackAnchorPoints", "heading", "indicatorTitle", "loading", "onClick", "onClickOutside", "onScroll", "open", "notifications", "unreadCount", "stylesheet"]);

  var className = otherProps.className;


  return React.createElement(
    NotificationFlyoutBehavior$1,
    {
      unreadCount: controlledUnreadCount,
      notifications: notificationsInput
    },
    function (_ref4) {
      var dismissNotification = _ref4.dismissNotification,
          handleClose = _ref4.handleClose,
          notifications = _ref4.notifications,
          showUnreadCount = _ref4.showUnreadCount,
          unreadCount = _ref4.unreadCount;
      return React.createElement(
        Flyout,
        {
          alterCoordinates: alterCoordinates,
          anchorPoint: anchorPoint,
          className: className,
          fallbackAnchorPoints: fallbackAnchorPoints,
          onClickOutside: onClickOutside,
          onClose: handleClose,
          onScroll: onScroll,
          open: open,
          panel: createPanelRenderer({
            emptyMessage: emptyMessage,
            dismissNotification: dismissNotification,
            notifications: notifications,
            loading: loading,
            heading: heading,
            stylesheet: stylesheet
          }),
          stylesheet: stylesheet
        },
        function (_ref5) {
          var handleClick = _ref5.handleClick;
          return React.createElement(IndicatorPresenter, {
            className: className,
            onClick: combineEventHandlers(onClick, handleClick),
            count: unreadCount,
            showCount: showUnreadCount,
            title: indicatorTitle,
            stylesheet: stylesheet
          });
        }
      );
    }
  );
}

NotificationsFlyout.defaultProps = {
  anchorPoint: anchorPoints.TOP_RIGHT
};

NotificationsFlyout.propTypes = {
  /** Manipulate flyout coordinates before each render */
  alterCoordinates: PropTypes.func,
  /** Where the flyout will be anchored relative to target */
  anchorPoint: PropTypes.oneOf(AVAILABLE_ANCHOR_POINTS),
  /** Rendered notifications. It can contain one or more <Notification /> components. */
  children: PropTypes.node,
  /** The message displayed when there are no notifications */
  emptyMessage: PropTypes.string,
  /**
   * When the flyout overflows the viewport, it'll attempt to
   * use the given anchor points in order to keep the flyout
   * within the viewport.
   */
  fallbackAnchorPoints: PropTypes.arrayOf(PropTypes.oneOf(AVAILABLE_ANCHOR_POINTS)),
  /** Flyout panel heading */
  heading: PropTypes.string,
  /** Indicator button title */
  indicatorTitle: PropTypes.string,
  /** Determines whether the loading animation is shown */
  loading: PropTypes.bool,
  /**
   * Rendered notifications.
   * It takes precedent over `children`, and accepts an array
   * containing any combination of <Notification /> components
   * and Notification models
   */
  notifications: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.node, PropTypes.object])),
  /** Function called when the flyout is opened */
  onClick: PropTypes.func,
  /** Function called when the flyout is open, and a click event occurs outside the flyout */
  onClickOutside: PropTypes.func,
  /** Function called when the flyout panel is scrolled */
  onScroll: PropTypes.func,
  /** When provided, it overrides the flyout's open state */
  open: PropTypes.bool,
  /** Function to modify the component's styles */
  stylesheet: PropTypes.func,
  /** When provided, it overrides the derived unread notification count */
  unreadCount: PropTypes.number
};
NotificationsFlyout.__docgenInfo = {
  "description": "",
  "displayName": "NotificationsFlyout",
  "props": {
    "alterCoordinates": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Manipulate flyout coordinates before each render"
    },
    "anchorPoint": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_ANCHOR_POINTS"
      },
      "required": false,
      "description": "Where the flyout will be anchored relative to target",
      "defaultValue": {
        "value": "anchorPoints.TOP_RIGHT",
        "computed": true
      }
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Rendered notifications. It can contain one or more <Notification /> components."
    },
    "emptyMessage": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "The message displayed when there are no notifications"
    },
    "fallbackAnchorPoints": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "enum",
          "computed": true,
          "value": "AVAILABLE_ANCHOR_POINTS"
        }
      },
      "required": false,
      "description": "When the flyout overflows the viewport, it'll attempt to\nuse the given anchor points in order to keep the flyout\nwithin the viewport."
    },
    "heading": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Flyout panel heading"
    },
    "indicatorTitle": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Indicator button title"
    },
    "loading": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Determines whether the loading animation is shown"
    },
    "notifications": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "union",
          "value": [{
            "name": "node"
          }, {
            "name": "object"
          }]
        }
      },
      "required": false,
      "description": "Rendered notifications.\nIt takes precedent over `children`, and accepts an array\ncontaining any combination of <Notification /> components\nand Notification models"
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function called when the flyout is opened"
    },
    "onClickOutside": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function called when the flyout is open, and a click event occurs outside the flyout"
    },
    "onScroll": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function called when the flyout panel is scrolled"
    },
    "open": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "When provided, it overrides the flyout's open state"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    },
    "unreadCount": {
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "When provided, it overrides the derived unread notification count"
    }
  }
};

NotificationsFlyout.Image = ImagePresenter;
NotificationsFlyout.Notification = Notification;

export default NotificationsFlyout;
export { ImagePresenter as Image, Notification, types, AVAILABLE_TYPES };
